import { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { useNotification } from '../../contexts/NotificationContext'
import { minoriApi, usersApi } from '../../services/api'
import { PageLoader, Card } from '../../components/ui'
import { 
  ArrowLeft, Save, Loader2, Check, Baby, Phone, MapPin, 
  Users, ChevronDown, ChevronUp, User, Calendar, FileText 
} from 'lucide-react'

export default function NewMinorePage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { isCoordinatore } = useAuth()
  const { showSuccess, showError } = useNotification()
  
  const isEditing = !!id
  
  const [loading, setLoading] = useState(isEditing)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [showContatti, setShowContatti] = useState(false)
  const [educatori, setEducatori] = useState([])
  
  const [formData, setFormData] = useState({
    nome: '',
    cognome: '',
    dataNascita: '',
    codiceFiscale: '',
    telefono: '',
    via: '',
    citta: '',
    cap: '',
    educatoreReferente: '',
    padreNome: '',
    padreTelefono: '',
    madreNome: '',
    madreTelefono: '',
    altroEtichetta: 'Altro',
    altroNome: '',
    altroTelefono: '',
    note: ''
  })

  // Carica educatori sempre
  useEffect(() => {
    loadEducatori()
  }, [])

  // Carica dati minore se in modalità edit
  useEffect(() => {
    if (isEditing) {
      loadMinore()
    }
  }, [id])

  const loadEducatori = async () => {
    try {
      const res = await usersApi.list({ attivo: true })
      const users = res.data?.data?.users || res.data?.data || res.data || []
      // Filtra solo educatori e coordinatori attivi
      const educatoriList = Array.isArray(users) ? users.filter(u => u.attivo !== false) : []
      setEducatori(educatoriList)
    } catch (error) {
      console.error('Errore caricamento educatori:', error)
    }
  }

  const loadMinore = async () => {
    try {
      setLoading(true)
      const res = await minoriApi.get(id)
      
      // Parsing robusto della risposta API
      const extractMinore = (response) => {
        const data = response?.data
        if (data?.data?.minore) return data.data.minore
        if (data?.minore) return data.minore
        if (data?.data && !data.data.minore) return data.data
        return data
      }
      
      const minore = extractMinore(res)
      
      console.log('Minore caricato:', minore) // Debug
      
      if (!minore || !minore._id) {
        showError('Minore non trovato')
        navigate('/minori')
        return
      }

      // Estrai nome e cognome da nomeCompleto se necessario
      let nome = minore.nome || ''
      let cognome = minore.cognome || ''
      
      if (!nome && !cognome && minore.nomeCompleto) {
        const parts = minore.nomeCompleto.trim().split(' ')
        nome = parts[0] || ''
        cognome = parts.slice(1).join(' ') || ''
      }

      // Estrai contatti famiglia
      const padre = minore.contattiFamiglia?.find(c => c.relazione === 'padre') || {}
      const madre = minore.contattiFamiglia?.find(c => c.relazione === 'madre') || {}
      const altro = minore.contattiFamiglia?.find(c => !['padre', 'madre'].includes(c.relazione)) || {}

      setFormData({
        nome: nome,
        cognome: cognome,
        dataNascita: minore.dataNascita ? minore.dataNascita.split('T')[0] : '',
        codiceFiscale: minore.codiceFiscale || '',
        telefono: minore.telefono || '',
        via: minore.indirizzo?.via || '',
        citta: minore.indirizzo?.citta || '',
        cap: minore.indirizzo?.cap || '',
        educatoreReferente: minore.educatoreReferente?._id || minore.educatoreReferente || '',
        padreNome: padre.nome || '',
        padreTelefono: padre.telefono || '',
        madreNome: madre.nome || '',
        madreTelefono: madre.telefono || '',
        altroEtichetta: altro.note || altro.relazione || 'Altro',
        altroNome: altro.nome || '',
        altroTelefono: altro.telefono || '',
        note: minore.note || ''
      })

      // Apri sezione contatti se ci sono dati
      if (padre.nome || madre.nome || altro.nome) {
        setShowContatti(true)
      }
    } catch (error) {
      console.error('Errore caricamento minore:', error)
      showError('Errore nel caricamento del minore')
      navigate('/minori')
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    // Validazione base
    if (!formData.nome.trim() || !formData.cognome.trim()) {
      showError('Nome e cognome sono obbligatori')
      return
    }

    setSaving(true)

    try {
      // Costruisci oggetto dati
      // Il backend usa nomeCompleto, non nome/cognome separati
      const nomeCompleto = `${formData.nome.trim()} ${formData.cognome.trim()}`.trim()
      
      const data = {
        nomeCompleto: nomeCompleto,
        nome: formData.nome.trim(),
        cognome: formData.cognome.trim(),
        dataNascita: formData.dataNascita || null,
        codiceFiscale: formData.codiceFiscale.toUpperCase() || null,
        telefono: formData.telefono || null,
        educatoreReferente: formData.educatoreReferente || null,
        indirizzo: {
          via: formData.via || null,
          citta: formData.citta || null,
          cap: formData.cap || null
        },
        contattiFamiglia: [],
        note: formData.note || null
      }

      // Aggiungi contatti famiglia
      if (formData.padreNome || formData.padreTelefono) {
        data.contattiFamiglia.push({
          relazione: 'padre',
          nome: formData.padreNome,
          telefono: formData.padreTelefono
        })
      }
      if (formData.madreNome || formData.madreTelefono) {
        data.contattiFamiglia.push({
          relazione: 'madre',
          nome: formData.madreNome,
          telefono: formData.madreTelefono
        })
      }
      if (formData.altroNome || formData.altroTelefono) {
        data.contattiFamiglia.push({
          relazione: 'altro',
          nome: formData.altroNome,
          telefono: formData.altroTelefono,
          note: formData.altroEtichetta !== 'Altro' ? formData.altroEtichetta : null
        })
      }

      if (isEditing) {
        await minoriApi.update(id, data)
        showSuccess('Minore aggiornato con successo')
      } else {
        await minoriApi.create(data)
        showSuccess('Minore creato con successo')
      }

      setSaved(true)
      setTimeout(() => {
        navigate(isEditing ? `/minori/${id}` : '/minori')
      }, 500)

    } catch (error) {
      console.error('Errore salvataggio:', error)
      showError(error.response?.data?.message || 'Errore nel salvataggio')
    } finally {
      setSaving(false)
    }
  }

  const handleBack = () => {
    if (isEditing) {
      navigate(`/minori/${id}`)
    } else {
      navigate('/minori')
    }
  }

  // Solo coordinatore può accedere
  if (!isCoordinatore()) {
    navigate('/minori')
    return null
  }

  if (loading) return <PageLoader />

  return (
    <div className="p-4 lg:p-6 pb-28">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <button onClick={handleBack} className="inline-flex items-center text-gray-500 hover:text-gray-700">
          <ArrowLeft className="h-4 w-4 mr-2" />
          {isEditing ? 'Torna al dettaglio' : 'Torna ai minori'}
        </button>
        <h1 className="text-xl font-bold text-gray-900 dark:text-white flex items-center">
          <Baby className="h-6 w-6 mr-2 text-primary-600" />
          {isEditing ? 'Modifica Minore' : 'Nuovo Minore'}
        </h1>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6 max-w-2xl mx-auto">
        {/* Dati Anagrafici */}
        <Card>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
            <User className="h-5 w-5 mr-2 text-primary-600" />
            Dati Anagrafici
          </h2>
          
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Nome <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.nome}
                  onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                  className="input"
                  placeholder="Nome"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Cognome <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.cognome}
                  onChange={(e) => setFormData({ ...formData, cognome: e.target.value })}
                  className="input"
                  placeholder="Cognome"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  <Calendar className="h-4 w-4 inline mr-1" />
                  Data di Nascita
                </label>
                <input
                  type="date"
                  value={formData.dataNascita}
                  onChange={(e) => setFormData({ ...formData, dataNascita: e.target.value })}
                  className="input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Codice Fiscale
                </label>
                <input
                  type="text"
                  value={formData.codiceFiscale}
                  onChange={(e) => setFormData({ ...formData, codiceFiscale: e.target.value.toUpperCase() })}
                  className="input"
                  maxLength={16}
                  placeholder="RSSMRA00A01H501Z"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                <Phone className="h-4 w-4 inline mr-1" />
                Telefono
              </label>
              <input
                type="tel"
                value={formData.telefono}
                onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                className="input"
                placeholder="+39 333 1234567"
              />
            </div>

            {/* Educatore Referente */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                <User className="h-4 w-4 inline mr-1" />
                Educatore Referente
              </label>
              <select
                value={formData.educatoreReferente}
                onChange={(e) => setFormData({ ...formData, educatoreReferente: e.target.value })}
                className="input"
              >
                <option value="">-- Nessun referente --</option>
                {educatori.map((edu) => (
                  <option key={edu._id} value={edu._id}>
                    {edu.nome} {edu.cognome} {edu.role === 'coordinatore' ? '(Coord.)' : ''}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                L'educatore di riferimento per questo minore
              </p>
            </div>
          </div>
        </Card>

        {/* Residenza */}
        <Card>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
            <MapPin className="h-5 w-5 mr-2 text-primary-600" />
            Residenza
          </h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Via/Indirizzo
              </label>
              <input
                type="text"
                value={formData.via}
                onChange={(e) => setFormData({ ...formData, via: e.target.value })}
                className="input"
                placeholder="Via Roma, 123"
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Città
                </label>
                <input
                  type="text"
                  value={formData.citta}
                  onChange={(e) => setFormData({ ...formData, citta: e.target.value })}
                  className="input"
                  placeholder="Roma"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  CAP
                </label>
                <input
                  type="text"
                  value={formData.cap}
                  onChange={(e) => setFormData({ ...formData, cap: e.target.value })}
                  className="input"
                  placeholder="00100"
                  maxLength={5}
                />
              </div>
            </div>
          </div>
        </Card>

        {/* Contatti Familiari - Collassabile */}
        <Card padding={false}>
          <button
            type="button"
            onClick={() => setShowContatti(!showContatti)}
            className="w-full px-4 lg:px-6 py-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
          >
            <span className="flex items-center font-semibold text-gray-900 dark:text-white">
              <Users className="h-5 w-5 mr-2 text-primary-600" />
              Contatti Familiari
            </span>
            {showContatti ? (
              <ChevronUp className="h-5 w-5 text-gray-400" />
            ) : (
              <ChevronDown className="h-5 w-5 text-gray-400" />
            )}
          </button>

          {showContatti && (
            <div className="px-4 lg:px-6 pb-4 lg:pb-6 pt-0 border-t border-gray-100 dark:border-gray-700 space-y-6">
              {/* Padre */}
              <div className="pt-4">
                <label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">
                  👨 Padre
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <input
                    type="text"
                    value={formData.padreNome}
                    onChange={(e) => setFormData({ ...formData, padreNome: e.target.value })}
                    className="input"
                    placeholder="Nome completo"
                  />
                  <input
                    type="tel"
                    value={formData.padreTelefono}
                    onChange={(e) => setFormData({ ...formData, padreTelefono: e.target.value })}
                    className="input"
                    placeholder="Telefono"
                  />
                </div>
              </div>

              {/* Madre */}
              <div>
                <label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">
                  👩 Madre
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <input
                    type="text"
                    value={formData.madreNome}
                    onChange={(e) => setFormData({ ...formData, madreNome: e.target.value })}
                    className="input"
                    placeholder="Nome completo"
                  />
                  <input
                    type="tel"
                    value={formData.madreTelefono}
                    onChange={(e) => setFormData({ ...formData, madreTelefono: e.target.value })}
                    className="input"
                    placeholder="Telefono"
                  />
                </div>
              </div>

              {/* Altro */}
              <div>
                <label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">
                  📋{' '}
                  <input
                    type="text"
                    value={formData.altroEtichetta}
                    onChange={(e) => setFormData({ ...formData, altroEtichetta: e.target.value })}
                    className="bg-transparent border-b border-gray-300 dark:border-gray-600 focus:border-primary-500 outline-none w-32 text-sm"
                    placeholder="Altro"
                  />
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <input
                    type="text"
                    value={formData.altroNome}
                    onChange={(e) => setFormData({ ...formData, altroNome: e.target.value })}
                    className="input"
                    placeholder="Nome completo"
                  />
                  <input
                    type="tel"
                    value={formData.altroTelefono}
                    onChange={(e) => setFormData({ ...formData, altroTelefono: e.target.value })}
                    className="input"
                    placeholder="Telefono"
                  />
                </div>
              </div>
            </div>
          )}
        </Card>

        {/* Note */}
        <Card>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
            <FileText className="h-5 w-5 mr-2 text-primary-600" />
            Note
          </h2>
          <textarea
            value={formData.note}
            onChange={(e) => setFormData({ ...formData, note: e.target.value })}
            className="input"
            rows={4}
            placeholder="Note aggiuntive sul minore..."
          />
        </Card>

        {/* Barra Fixed Pulsanti */}
        <div className="fixed left-0 right-0 bottom-16 lg:bottom-0 lg:left-64 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 z-40 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)]">
          <div className="max-w-2xl mx-auto flex justify-end space-x-3 p-4">
            <button
              type="button"
              onClick={handleBack}
              className="btn-secondary px-5 py-2.5 text-base"
            >
              Annulla
            </button>
            <button
              type="submit"
              disabled={saving}
              className="btn-primary flex items-center px-5 py-2.5 text-base"
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Salvataggio...
                </>
              ) : saved ? (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  Salvato!
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  {isEditing ? 'Aggiorna' : 'Crea Minore'}
                </>
              )}
            </button>
          </div>
        </div>
      </form>
    </div>
  )
}
